/**
 * Version History Panel — View, create, restore, and deploy website versions.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRepairPackageJson } from '../../hooks/useRepairPackageJson';
import {
  X,
  History,
  Plus,
  RotateCcw,
  Trash2,
  Rocket,
  Tag,
  Loader2,
  Check,
  Clock,
  Globe,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useConfirm } from '@/components/ui/confirm-provider';

interface VersionHistoryProps {
  projectId: string;
  jobId: string;
  onContentUpdated: () => void;
  onClose: () => void;
}

interface Version {
  id: string;
  jobId: string;
  versionNumber: number;
  label: string;
  type: 'auto' | 'named';
  snapshotKey: string;
  changes?: {
    summary: string;
    details: string[];
  };
  createdBy: string;
  createdAt: string;
}

interface DeploymentInfo {
  id: string;
  status: string;
  subdomain: string;
  publishedUrl: string;
  versionNumber: number;
  versionLabel?: string;
  filesDeployed: number;
  deployedAt: string;
}

export function VersionHistory({
  projectId,
  jobId,
  onContentUpdated,
  onClose,
}: VersionHistoryProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // Fetch versions
  const { data: versions, isLoading: versionsLoading } = useQuery({
    queryKey: ['versions', projectId, jobId],
    queryFn: async () => {
      const res = await api.get(`/deployment/${projectId}/${jobId}/versions`);
      return (res.data.data || []) as Version[];
    },
  });

  // Fetch current deployment status
  const { data: deployment, isLoading: deploymentLoading } = useQuery({
    queryKey: ['deployment-status', projectId],
    queryFn: async () => {
      const res = await api.get(`/deployment/${projectId}/status`);
      return res.data.data as DeploymentInfo | null;
    },
  });

  // Create version
  const createVersion = useMutation({
    mutationFn: async (label: string) => {
      const res = await api.post(`/deployment/${projectId}/${jobId}/versions`, { label });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', projectId, jobId] });
      toast.success('Version saved');
      setShowCreateForm(false);
      setNewLabel('');
    },
    onError: () => toast.error('Failed to create version'),
  });

  // Restore version
  const restoreVersion = useMutation({
    mutationFn: async (versionNumber: number) => {
      const res = await api.post(
        `/deployment/${projectId}/${jobId}/versions/${versionNumber}/restore`
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['versions', projectId, jobId] });
      onContentUpdated();
      toast.success(`Restored to version (${data.filesRestored} files)`);
    },
    onError: () => toast.error('Failed to restore version'),
  });

  // Delete version
  const deleteVersion = useMutation({
    mutationFn: async (versionNumber: number) => {
      await api.delete(`/deployment/${projectId}/${jobId}/versions/${versionNumber}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', projectId, jobId] });
      toast.success('Version deleted');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to delete version'),
  });

  // Deploy
  const deploy = useMutation({
    mutationFn: async (versionNumber?: number) => {
      const res = await api.post(`/deployment/${projectId}/deploy`, {
        jobId,
        versionNumber,
      });
      return res.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deployment-status', projectId] });
      toast.success(
        <div>
          <strong>Deployed!</strong>
          <br />
          <a
            href={data.publishedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline"
          >
            {data.publishedUrl}
          </a>
        </div>
      );
    },
    onError: () => toast.error('Deployment failed'),
  });

  // Undeploy
  const undeploy = useMutation({
    mutationFn: async () => {
      await api.post(`/deployment/${projectId}/undeploy`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deployment-status', projectId] });
      toast.success('Site taken offline');
    },
    onError: () => toast.error('Failed to take site offline'),
  });

  // Auto-repair package.json on the project's GitHub repo. Shared with the
  // build-failure banner so users can trigger the same fix from either place.
  const repairPkgJson = useRepairPackageJson(projectId);

  return (
    <div className="flex w-80 flex-col border-l bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold">Versions & Deploy</span>
        </div>
        <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      {/* Deployment status card */}
      <div className="border-b px-4 py-3">
        {deploymentLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking deployment...
          </div>
        ) : deployment && deployment.status === 'live' ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
              <span className="text-xs font-medium text-green-700">Live</span>
              <span className="text-xs text-gray-400">v{deployment.versionNumber}</span>
            </div>
            <a
              href={deployment.publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <Globe className="h-3 w-3" />
              {deployment.subdomain}.sites.tyashin.com
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
            <div className="flex gap-2">
              <button
                onClick={() => deploy.mutate(undefined)}
                disabled={deploy.isPending}
                className="flex-1 rounded-md bg-blue-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {deploy.isPending ? (
                  <Loader2 className="mx-auto h-3 w-3 animate-spin" />
                ) : (
                  'Update Deploy'
                )}
              </button>
              <button
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Take site offline?',
                      message: 'Take this site offline?',
                      danger: true,
                    })
                  ) {
                    undeploy.mutate();
                  }
                }}
                disabled={undeploy.isPending}
                className="rounded-md border px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Offline
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-gray-300" />
              <span className="text-xs text-gray-500">Not deployed</span>
            </div>
            <button
              onClick={() => deploy.mutate(undefined)}
              disabled={deploy.isPending}
              className="flex w-full items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {deploy.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Rocket className="h-3 w-3" />
                  Deploy to Live
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Build troubleshooting — auto-repair common build failures without
          regenerating the entire site. Currently: package.json sanitizer. */}
      <div className="border-b px-4 py-3">
        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-700">
          <Wrench className="h-3 w-3 text-amber-600" />
          Build troubleshooting
        </div>
        <p className="mb-2 text-[11px] leading-snug text-gray-500">
          Build failing at <code className="rounded bg-gray-100 px-1">npm install</code> with
          EINVALIDPACKAGENAME / EINVALIDTAGNAME? Auto-repair{' '}
          <code className="rounded bg-gray-100 px-1">package.json</code> on the default branch.
        </p>
        <button
          onClick={() => repairPkgJson.mutate()}
          disabled={repairPkgJson.isPending}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {repairPkgJson.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <Wrench className="h-3 w-3" />
              Repair package.json
            </>
          )}
        </button>
      </div>

      {/* Version actions */}
      <div className="border-b px-4 py-2">
        {showCreateForm ? (
          <div className="space-y-2">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Version label (optional)"
              className="w-full rounded-md border px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => createVersion.mutate(newLabel || '')}
                disabled={createVersion.isPending}
                className="flex-1 rounded-md bg-green-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {createVersion.isPending ? (
                  <Loader2 className="mx-auto h-3 w-3 animate-spin" />
                ) : (
                  'Save Version'
                )}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="rounded-md border px-2 py-1.5 text-xs hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600"
          >
            <Plus className="h-3 w-3" />
            Save Current Version
          </button>
        )}
      </div>

      {/* Version list */}
      <div className="flex-1 overflow-y-auto">
        {versionsLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}

        {!versionsLoading && (!versions || versions.length === 0) && (
          <p className="py-8 text-center text-xs text-gray-400">
            No versions yet. Save your first version above.
          </p>
        )}

        {versions?.map((v) => {
          const isExpanded = expandedVersion === v.versionNumber;
          const isDeployed =
            deployment?.status === 'live' && deployment?.versionNumber === v.versionNumber;

          return (
            <div
              key={v.versionNumber}
              className={`border-b transition-colors ${isDeployed ? 'bg-green-50' : ''}`}
            >
              <div
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 hover:bg-gray-50"
                onClick={() => setExpandedVersion(isExpanded ? null : v.versionNumber)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                ) : (
                  <ChevronRight className="h-3 w-3 flex-shrink-0 text-gray-400" />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-700">v{v.versionNumber}</span>
                    {v.type === 'named' && <Tag className="h-2.5 w-2.5 text-blue-500" />}
                    {isDeployed && (
                      <span className="rounded bg-green-100 px-1 py-0.5 text-[9px] font-medium text-green-700">
                        LIVE
                      </span>
                    )}
                  </div>
                  <p className="truncate text-[10px] text-gray-500">
                    {v.label || v.changes?.summary || 'Auto-saved version'}
                  </p>
                </div>

                <div className="flex flex-shrink-0 items-center gap-1 text-[10px] text-gray-400">
                  <Clock className="h-2.5 w-2.5" />
                  {formatRelativeTime(v.createdAt)}
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="space-y-2 border-t bg-gray-50 px-4 py-2.5">
                  {v.changes?.details && (
                    <ul className="space-y-0.5 text-[10px] text-gray-500">
                      {v.changes.details.map((d, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <Check className="mt-0.5 h-2.5 w-2.5 flex-shrink-0 text-green-500" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex gap-1.5 pt-1">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          await confirm({
                            title: 'Restore version?',
                            message: `Restore to version ${v.versionNumber}?`,
                          })
                        ) {
                          restoreVersion.mutate(v.versionNumber);
                        }
                      }}
                      disabled={restoreVersion.isPending}
                      className="flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] text-gray-600 hover:bg-white disabled:opacity-50"
                    >
                      <RotateCcw className="h-2.5 w-2.5" />
                      Restore
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deploy.mutate(v.versionNumber);
                      }}
                      disabled={deploy.isPending}
                      className="flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[10px] text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Rocket className="h-2.5 w-2.5" />
                      Deploy
                    </button>

                    {v.versionNumber !== versions?.[0]?.versionNumber && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              title: 'Delete version?',
                              message: 'Delete this version?',
                              danger: true,
                            })
                          ) {
                            deleteVersion.mutate(v.versionNumber);
                          }
                        }}
                        disabled={deleteVersion.isPending}
                        className="flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-[10px] text-red-500 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
